import { autoLoad, clearAllStorage, removeFromStorage, saveToStorage } from '@helpers/storage.helpers'
import { Store } from '@stores'
import { IOVErrorResponse, IUserLoginCreds, IUserRegister } from '@typings'
import { action, computed, makeAutoObservable } from 'mobx'
import { toast } from 'react-toastify'
import { EVENT_KEYS, STORE_KEYS } from 'src/constants/keys.contants'
import { IAuthUser } from 'src/typings/auth.typings'

export class AuthStore {
  [key: string]: any
  store: Store
  loading: boolean
  user: Omit<IAuthUser, 'access_token'>
  access_token: string | null
  refresh_token: string | null

  constructor(store: Store) {
    makeAutoObservable(this)
    this.store = store
    this.sessionToken = null
    this.user = {} as Omit<IAuthUser, 'access_token'>
    this.userRole = null
    this.loading = false
    this.access_token = null
    this.refresh_token = null
    autoLoad(this, STORE_KEYS.ACCESS_TOKEN)
  }

  @computed
  get is_authenticated(): boolean {
    return this.access_token !== null
  }

  @action
  register = async (data: IUserRegister) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const _data = await this.store.api.auth.register(data)
      console.log('test123: ', _data)
      toast('Je bent succesvol geregisteerd.', { type: 'success' })
      return Promise.resolve(_data)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  handleInitialCalls = async () => {
    try {
      await this.store.company.getMyCompany()
      return Promise.resolve()
    } catch (e) {
      return Promise.reject(e)
    }
  }

  @action
  login = async (data: IUserLoginCreds, callback?: () => void) => {
    this.set(STORE_KEYS.LOADING, true)
    const { email, password } = data
    try {
      const { access_token, ...rest } = await this.store.api.auth.login({
        email,
        password,
      })
      removeFromStorage(EVENT_KEYS.UNAUTH)
      await this.set(STORE_KEYS.ACCESS_TOKEN, access_token, true)
      await this.store.user.set(STORE_KEYS.USER, rest, true)
      await this.handleInitialCalls()
      if (callback) callback()
      return Promise.resolve()
    } catch (e: any & IOVErrorResponse) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  refreshToken = async (newToken: string) => {
    this.set(STORE_KEYS.ACCESS_TOKEN, newToken, true)
  }

  @action
  handleLogOut = () => {
    this.store.ui
      .confirm({
        title: 'Weet je zeker dat je wilt uitloggen?',
        content: 'Je staat op het punt om uit te loggen.',
        confirmLabel: 'Log uit',
        withCancel: true,
      })
      .then(() => {
        this.logout()
      })
      .catch(() => void 0)
  }

  @action
  logout = async (fromEvent?: boolean) => {
    this.clearStorage()
    this.set(STORE_KEYS.REFRESH_TOKEN, null)
    this.set(STORE_KEYS.ACCESS_TOKEN, null)
    if (fromEvent) {
      saveToStorage(EVENT_KEYS.UNAUTH, true)
      this.store.ui.handleErrorToast('Je bent uitgelogd omdat je sessie is verlopen. Log opnieuw in.')
    }
  }

  @action
  clearStorage = () => {
    clearAllStorage()
  }

  @action
  forgotPassword = async (email: string) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const response = await this.store.api.auth.forgotPassword(email)
      return Promise.resolve(response)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  resetPassword = async (data: { email: string; password: string; password_confirmation: string; token: string }) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const response = await this.store.api.auth.resetPassword(data)
      return Promise.resolve(response)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  verifyEmail = async (id: string, hash: string) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const response = await this.store.api.auth.verifyEmail(id, hash)
      return Promise.resolve(response)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  set = (target: string, value: unknown, save?: boolean): unknown => {
    if (!target) return
    this[target] = value
    if (save) saveToStorage(target, value)
  }
}
